import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Typography, Grid, LinearProgress } from '@mui/material';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import ReactAudioPlayer from 'react-audio-player';
import axios from 'axios';
import { decrypt } from '../../../Security';

const AudioUploadComponent = ({ uploadedFiles, setUploadedFiles, handleDeleteUpload, createMode }) => {
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [timer, setTimer] = useState(0); 
  const mediaRecorderRef = useRef(null);
  const intervalRef = useRef(null);

  // Handle file selection
  const handleAudioChange = async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      const fileUrl = URL.createObjectURL(file);

      // Avoid duplicates by checking if the file URL already exists in the array
      if (!uploadedFiles.includes(fileUrl)) {
        await handleUpload(file);
        // setUploadedFiles((prev) => [...prev, fileUrl]);
      }
    }

    setRecording(false);
    setTimer(0);
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        chunks.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(blob);

        // Avoid duplicates by checking if the file URL already exists in the array
        if (!uploadedFiles.includes(audioUrl)) {
          // setUploadedFiles((prev) => [...prev, audioUrl]);
          handleUpload(blob);
        }

        clearInterval(intervalRef.current);
      };

      mediaRecorderRef.current.start();
      setRecording(true);

      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing audio devices.', error);
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
    clearInterval(intervalRef.current);
  };

  const handleDelete = async (fileUrl) => {
    setUploadedFiles((prev) => prev.filter((url) => url !== fileUrl));
    await handleDeleteUpload(fileUrl);
  };

  const handleUpload = async (recordedBlob) => {
    if (!recordedBlob) {
      console.error('No audio available to upload');
      return;
    }

    const formData = new FormData();
    formData.append('audio', recordedBlob, recordedBlob.name || 'recorded-audio.webm');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_PUBLIC_SERVER_URI}/api/v1/media/upload-audio`,
        formData,
        {
          withCredentials: true,
        }
      );

      const audioUrl = decrypt(response.data).audioUrl;

      // Avoid duplicates by checking if the file URL already exists in the array
      if (!uploadedFiles.includes(audioUrl)) {
        setUploadedFiles((prev) => [...prev, audioUrl]);
      }
    } catch (error) {
      console.error('Error uploading audio:', error);
    }
  };

  return (
    <Box
      sx={{
        padding: 2,
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      {uploadedFiles.length > 0 && (
        <Box sx={{ width: '100%', mb: 2 }}>
          {uploadedFiles.map((fileUrl, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ReactAudioPlayer src={fileUrl} controls style={{ width: '100%' }} />
              { <Button
                variant="contained"
                color="error"
                sx={{ ml: 2 }}
                onClick={() => handleDelete(fileUrl)}
              >
                Delete
              </Button>}
            </Box>
          ))}
        </Box>
      )}

      {(
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
            Upload or Record Audio Files {!createMode && "to this Post"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button variant="outlined" component="label" color="success" fullWidth>
                Choose Files
                <input
                  type="file"
                  accept="audio/*"
                  hidden
                  multiple
                  onChange={handleAudioChange}
                />
              </Button>
            </Grid>
            <Grid item xs={6}>
              {recording ? (
                <Button variant="contained" color="error" fullWidth onClick={stopRecording}>
                  Stop Recording
                </Button>
              ) : (
                <Button variant="contained" color="success" fullWidth onClick={startRecording}>
                  Record Audio
                </Button>
              )}
            </Grid>
          </Grid>
          {recording && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                Recording Time: {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default AudioUploadComponent;
